import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const MARKET_PRICE_KEYS = [
  'tge_price',
  'energy_price',
  'price_pln_mwh',
  'price_pln_kwh',
];

const FULL_PRICE_KEYS = [
  'full_price',
  'total_price',
  'price_gross',
  'gross_price',
  'price',
  'price_net',
  'net_price',
  'unit_price',
];

const PRICE_MODES = {
  market: {
    label: 'Cena rynkowa',
    tooltip: 'Cena energii na rynku hurtowym (główny składnik pełnego kosztu).',
  },
  full: {
    label: 'Pełny koszt',
    tooltip: 'Całkowity koszt, uwzględniający opłaty za energię z obsługą, dystrybucję, akcyzę i VAT.',
  },
};

const formatHour = (hour) => `${String(hour).padStart(2, '0')}:00`;

const formatDate = (dateValue) => {
  if (!dateValue) return '';
  return new Date(`${dateValue}T12:00:00`).toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: 'long',
  });
};

const getWarsawDateHour = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date()).reduce((acc, part) => {
    acc[part.type] = part.value;
    return acc;
  }, {});

  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour),
  };
};

const findPrice = (value, keys) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (!value || typeof value !== 'object') return null;

  for (const key of keys) {
    if (typeof value[key] === 'number' && Number.isFinite(value[key])) {
      return value[key];
    }
  }

  return null;
};

const getFramePrices = (frame) => {
  const pricing = frame?.metrics?.pricing ?? frame?.pricing;
  if (!pricing) {
    return {
      marketPrice: null,
      fullPrice: null,
    };
  }

  return {
    marketPrice: findPrice(pricing, MARKET_PRICE_KEYS),
    fullPrice: findPrice(pricing, FULL_PRICE_KEYS),
  };
};

const normalizeFrames = (frames = [], timezone = 'Europe/Warsaw') => frames
  .map((frame) => {
    const startDate = new Date(frame.start);
    if (Number.isNaN(startDate.getTime())) return null;

    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(startDate).reduce((acc, part) => {
      acc[part.type] = part.value;
      return acc;
    }, {});

    const prices = getFramePrices(frame);

    return {
      date: `${parts.year}-${parts.month}-${parts.day}`,
      hour: Number(parts.hour),
      ...prices,
    };
  })
  .filter(Boolean);

const getPriceColor = (price, min, max) => {
  if (typeof price !== 'number') return 'rgba(128, 128, 128, 0.25)';
  if (price < 0) return 'rgba(39, 205, 138, 0.92)';
  if (max <= min) return 'rgba(113, 214, 143, 0.55)';

  const rawRatio = (price - min) / (max - min);
  const ratio = Math.max(0, Math.min(1, rawRatio * 0.82));
  const r = Math.round(80 + ratio * 155);
  const g = Math.round(218 - ratio * 92);
  const b = Math.round(118 - ratio * 50);
  return `rgba(${r}, ${g}, ${b}, 0.8)`;
};

const getPriceBadge = (price) => {
  if (typeof price !== 'number') return null;
  if (price < 0) {
    return {
      label: 'ZARABIASZ NA PRĄDZIE!',
      color: '#ffffff',
      backgroundColor: '#062f1f',
      borderColor: 'rgba(255,255,255,0.5)',
    };
  }
  if (price < 0.15) {
    return {
      label: 'SUPER!',
      color: '#ffffff',
      backgroundColor: '#12321f',
      borderColor: 'rgba(255,255,255,0.36)',
    };
  }
  if (price < 0.3) {
    return {
      label: 'DOBRA CENA!',
      color: '#111111',
      backgroundColor: 'rgba(255,255,255,0.7)',
      borderColor: 'rgba(0,0,0,0.18)',
    };
  }
  return null;
};

const formatPrice = (price) => {
  if (typeof price !== 'number') return '-';

  const unit = Math.abs(price) >= 10 ? 'PLN/MWh' : 'PLN/kWh';
  const digits = 2;
  return `${price.toLocaleString('pl-PL', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ${unit}`;
};

const renderPrice = (price) => {
  const badge = getPriceBadge(price);

  return (
    <>
      {formatPrice(price)}
      {badge && (
        <Box
          component="span"
          sx={{
            color: badge.color,
            backgroundColor: badge.backgroundColor,
            border: `1px solid ${badge.borderColor}`,
            borderRadius: '4px',
            display: 'inline-block',
            fontSize: '0.68em',
            fontWeight: 900,
            ml: 0.5,
            px: 0.45,
            py: 0.05,
            whiteSpace: 'nowrap',
          }}
        >
          {badge.label}
        </Box>
      )}
    </>
  );
};

const PstrykPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSlot, setCurrentSlot] = useState(getWarsawDateHour);
  const [priceMode, setPriceMode] = useState('market');
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  useEffect(() => {
    let active = true;

    axios.get(`${apiBaseUrl}/api/pstryk/prices`)
      .then((response) => {
        if (!active) return;
        setData(response.data);
        setError(null);
      })
      .catch((err) => {
        if (!active) return;
        setError(err.response?.data?.error || 'Nie udało się pobrać cen prądu z API Pstryk.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [apiBaseUrl]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentSlot(getWarsawDateHour());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  const rows = useMemo(() => {
    const frames = normalizeFrames(data?.frames, data?.timezone);
    const byDateAndHour = new Map(frames.map((frame) => [`${frame.date}-${frame.hour}`, frame]));
    const prices = frames
      .map((frame) => frame.marketPrice)
      .filter((price) => typeof price === 'number');
    const min = prices.length ? Math.min(...prices) : 0;
    const max = prices.length ? Math.max(...prices) : 0;

    return Array.from({ length: 24 }, (_, hour) => {
      const todayFrame = byDateAndHour.get(`${data?.today}-${hour}`);
      const tomorrowFrame = byDateAndHour.get(`${data?.tomorrow}-${hour}`);
      const todayPrice = priceMode === 'full' ? todayFrame?.fullPrice : todayFrame?.marketPrice;
      const tomorrowPrice = priceMode === 'full' ? tomorrowFrame?.fullPrice : tomorrowFrame?.marketPrice;
      return {
        hour,
        todayPrice,
        tomorrowPrice,
        todayColor: getPriceColor(todayFrame?.marketPrice, min, max),
        tomorrowColor: getPriceColor(tomorrowFrame?.marketPrice, min, max),
        isCurrentToday: currentSlot.date === data?.today && currentSlot.hour === hour,
        isCurrentTomorrow: currentSlot.date === data?.tomorrow && currentSlot.hour === hour,
      };
    });
  }, [currentSlot, data, priceMode]);

  const handlePriceModeChange = (_event, nextMode) => {
    if (nextMode) {
      setPriceMode(nextMode);
    }
  };

  const currentPriceSx = {
    outline: '3px solid #ffffff',
    outlineOffset: '-4px',
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.55), 0 0 0 2px rgba(255,255,255,0.24)',
  };

  const hourCellSx = {
    width: 56,
    backgroundColor: 'background.paper',
    fontWeight: 'bold',
    fontSize: { xs: '0.72rem', sm: '0.82rem' },
    px: { xs: 0.5, sm: 0.75 },
    py: 0.75,
  };

  const priceCellSx = {
    width: { xs: 98, sm: 116 },
    color: 'black',
    fontWeight: 'bold',
    fontSize: { xs: '0.72rem', sm: '0.84rem' },
    px: { xs: 0.5, sm: 0.75 },
    py: 0.75,
    textShadow: '0.5px 0.5px 1px rgba(255,255,255,0.35)',
  };

  const renderHourCell = (hour, withDivider = false) => (
    <TableCell
      align="center"
      sx={{
        ...hourCellSx,
        borderLeft: withDivider ? '2px solid rgba(255,255,255,0.16)' : undefined,
      }}
    >
      {formatHour(hour)}
    </TableCell>
  );

  const renderPriceCell = (row, day, withDivider = false) => {
    const isToday = day === 'today';
    const price = isToday ? row.todayPrice : row.tomorrowPrice;
    const backgroundColor = isToday ? row.todayColor : row.tomorrowColor;
    const isCurrent = isToday ? row.isCurrentToday : row.isCurrentTomorrow;

    return (
      <TableCell
        align="center"
        sx={{
          ...priceCellSx,
          backgroundColor,
          borderLeft: withDivider ? '2px solid rgba(255,255,255,0.16)' : undefined,
          ...(isCurrent ? currentPriceSx : {}),
        }}
      >
        {renderPrice(price)}
      </TableCell>
    );
  };

  return (
    <>
      <Helmet>
        <title>Ceny prądu dziś i jutro | SymuLiga x Pstryk</title>
        <meta
          name="description"
          content="Godzinowa tabela cen prądu na dziś i jutro z Pstryk oraz kod zniżkowy SYMULIGA50."
        />
      </Helmet>

      <Box sx={{ padding: { xs: '0px', sm: '5px', md: '10px' } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            mb: 3,
            gap: 1,
          }}
        >
          <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
            Ceny prądu Pstryk - dziś i jutro
          </Typography>
          <Typography variant="subtitle1" component="h2" sx={{ color: 'text.secondary', maxWidth: 760 }}>
            Godzinowe ceny energii w Pstryk.
          </Typography>
          <ToggleButtonGroup
            value={priceMode}
            exclusive
            onChange={handlePriceModeChange}
            size="small"
            sx={{
              mt: 1,
              backgroundColor: 'rgba(255,255,255,0.06)',
              '& .MuiToggleButton-root': {
                color: 'text.primary',
                borderColor: 'rgba(255,255,255,0.18)',
                gap: 0.5,
                px: { xs: 1, sm: 1.5 },
                textTransform: 'none',
              },
              '& .Mui-selected': {
                backgroundColor: 'rgba(113, 214, 143, 0.28) !important',
              },
            }}
          >
            {Object.entries(PRICE_MODES).map(([mode, config]) => (
              <ToggleButton key={mode} value={mode}>
                {config.label}
                <Tooltip title={config.tooltip} arrow>
                  <InfoOutlinedIcon
                    fontSize="inherit"
                    sx={{ ml: 0.35, color: 'text.secondary' }}
                    onClick={(event) => event.stopPropagation()}
                  />
                </Tooltip>
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {loading && (
          <Typography variant="h6" sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Ładowanie...
          </Typography>
        )}

        {error && !loading && (
          <Typography variant="h6" sx={{ textAlign: 'center', color: '#ff8a80', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                marginBottom: 2,
                width: 'fit-content',
                maxWidth: '100%',
                display: 'inline-block',
                overflowX: 'auto',
                borderRadius: '12px',
                position: 'relative',
                '&::-webkit-scrollbar': {
                  width: '8px',
                  height: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  borderRadius: '4px',
                },
              }}
            >
              <Table sx={{ width: 'auto', tableLayout: 'fixed', position: 'relative', zIndex: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', fontSize: { xs: '0.86rem', sm: '1rem' } }}>
                      Dziś<br />{formatDate(data?.today)}
                    </TableCell>
                    <TableCell
                      align="center"
                      colSpan={4}
                      sx={{
                        fontWeight: 'bold',
                        fontSize: { xs: '0.86rem', sm: '1rem' },
                        borderLeft: '2px solid rgba(255,255,255,0.16)',
                      }}
                    >
                      Jutro<br />{formatDate(data?.tomorrow)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    {['Godz.', 'Cena', 'Godz.', 'Cena', 'Godz.', 'Cena', 'Godz.', 'Cena'].map((label, index) => (
                      <TableCell
                        key={`${label}-${index}`}
                        align="center"
                        sx={{
                          width: label === 'Godz.' ? 56 : { xs: 98, sm: 116 },
                          fontWeight: 'bold',
                          fontSize: { xs: '0.68rem', sm: '0.78rem' },
                          px: { xs: 0.5, sm: 0.75 },
                          borderLeft: index === 4 ? '2px solid rgba(255,255,255,0.16)' : undefined,
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 12 }, (_, hour) => {
                    const morning = rows[hour];
                    const afternoon = rows[hour + 12];

                    return (
                      <TableRow key={hour}>
                        {renderHourCell(hour)}
                        {renderPriceCell(morning, 'today')}
                        {renderHourCell(hour + 12)}
                        {renderPriceCell(afternoon, 'today')}
                        {renderHourCell(hour, true)}
                        {renderPriceCell(morning, 'tomorrow')}
                        {renderHourCell(hour + 12)}
                        {renderPriceCell(afternoon, 'tomorrow')}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Paper
          sx={{
            mt: 2,
            mx: 'auto',
            p: { xs: 2, sm: 2.5 },
            maxWidth: 760,
            borderRadius: '12px',
            backgroundColor: 'rgba(255,255,255,0.06)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <BoltIcon fontSize="small" />
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              Jak mieć takie ceny u siebie?
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            1. Wejdź na pstryk.pl
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            2. Sprawdź ofertę z ceną zmienną godzinowo
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            3. Podpisz umowę online. Przy rejestracji podaj kod SYMULIGA50, aby uzyskać 50 zł zniżki
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            4. Po uruchomieniu usługi możesz planować zużycie prądu wtedy, gdy energia jest najtańsza.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.25 }}>
            <Chip
              icon={<LocalOfferIcon />}
              label="SYMULIGA50"
              sx={{ fontWeight: 'bold', backgroundColor: 'rgba(113, 214, 143, 0.22)' }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Kod daje 50 zł rabatu przy podpisaniu umowy.
            </Typography>
            <Button
              component={Link}
              href="https://pstryk.pl"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              size="small"
              endIcon={<OpenInNewIcon />}
              sx={{ ml: { xs: 0, sm: 'auto' }, textTransform: 'none' }}
            >
              Przejdź do Pstryk
            </Button>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default PstrykPage;
